import { EntityType } from "./EntityType";
import { Patient } from "./Patient"
import { Prescriber } from "./Prescriber"
import { Prescription } from "./Prescription"
import HasLabel from "@/models/HasLabel";
import { Program } from "./Program";
import { DrugProgram } from "./Drug/Drug";
import { CustomFaxTemplateBase } from "./FaxTemplates";
import { AcceptanceConfiguration } from "./AcceptanceConfiguration";
import { PatientDocument } from "./PatientDocument";
import { PharmacyRecord } from "./Pharmacy";
import { TaskIngestionData } from "./TaskIngestionData";
import TextUtils from "@/util/TextUtils";

export interface UserTask {
    id: number
    title: string
    description: string
    startDate: Date
    dueDate: Date
    storeId: number
    status: UserTaskStatus
    created: Date
    createdBy: string
    updated: Date
    updatedBy: string

    prescriptions: Prescription[]
    patients: Patient[]
    assignments: UserTaskAssignment[]
    watchers: UserTaskAssignment[]
    storeName?: string

    enabled: boolean
    isScheduled: boolean
    repeatMode?: UserTaskRepeatMode
    repeatSchedule?: string

    isTemplate: boolean
    dueAtTime: boolean
    taskCompletionQuestionSetId?: number

    completionQuestionSet?: UserTaskQuestionSet
    completionAnswers?: UserTaskAnswer[]

    createdByTriggerId?: number
    createdByTemplateId?: number
    expiredOnDate?: Date

    limitToTeamIds?: number[]
    limitToProgramIds?: number[]
    limitToRxStatuses?: number[]

    blockCheckout: boolean
    taskCompletionFormId?: number
    taskCompletionForm?: CustomFaxTemplateBase

    completionFormImageAccepted: boolean | null
    completionFormImageId: number | null
    signatureData?: string
    signatureDate?: Date
    hasUnansweredQuestions?: boolean | null

    autoOutboundEnabled?: boolean
    autoOutboundContactDelay?: number
    autoOutboundMaxAge?: number
    autoOutboundTwilioFlow?: string

    outreachEnabled?: boolean
    outreachContactDelay?: number
    outreachMaxAttempts?: number
    outreachTextTemplate?: string

    expiryFollowUpTemplateId?: number
    patientDocuments?: PatientDocument[]

    storedPharmacyId?: number
    storedPharmacy?: PharmacyRecord

    ingestionData?: TaskIngestionData

    // SD-3303
    cancelled?: Date | null
    cancelledBy?: string | null
    cancellationReason?: string | null
    /**
     * On a template (isTemplate=true): when set, forces the cancel UX to require a reason
     * regardless of the global RequireTaskCancellationReason config. Null defers to the global.
     */
    requireCancellationReasonOverride?: boolean | null
    lockedBy?: string
    lockExpires?: string

    /**
     * On a template or scheduled task: how the Start Date -> Due Date day offset is counted when a
     * task is created from it. Null/undefined means calendar days. Ignored on regular tasks.
     */
    dueDateOffsetUnit?: TaskDateOffsetUnit | null
    /** As dueDateOffsetUnit, for the Start Date -> Expires On day offset. */
    expirationOffsetUnit?: TaskDateOffsetUnit | null
}

export class UserTask extends HasLabel {

    constructor(obj?: UserTask) {
        super();
        if (obj) Object.assign(this, obj);

        if (!this.completionQuestionSet) {
            this.completionQuestionSet = {
                questions: [],
                entityType: EntityType.UserTask,
                active: true
            };
        }
    }

    toString(): string {
        let label = "";
        if (this.id) {
            label =
                "<b>" +
                this.title +
                "</b> - " +
                                " <small>(ID: " +
                this.id+")</small>";
        }
        if (label == '') {
            label = '<div class="font-weight-light">Task</div>';
        }
        return label;
    }

}

export enum UserTaskStatus {
    new,
    inProgress,
    complete,
    expired,
    cancelled
}

/**
 * Display text for a task status. The enum members are camelCase, so anything showing a
 * status to a user reads it through here rather than reverse-mapping the enum directly, which
 * would render "inProgress". Shared by the task badge and the History page's status formatter
 * so the two surfaces cannot drift apart.
 */
export function taskStatusLabel(status: UserTaskStatus): string {
    return TextUtils.camelCaseToNormal(UserTaskStatus[status]);
}

/**
 * Unit for the day offsets a template (or scheduled task) applies to the tasks it spawns.
 * Values mirror the TINYINT persisted on Tasks.
 */
export enum TaskDateOffsetUnit {
    calendarDays = 0,
    businessDays = 1
}

/** Options for the Due Date / Expires unit selectors on a template. */
export const taskDateOffsetUnitOptions = [
    { text: 'Calendar Day(s) After Creation', value: TaskDateOffsetUnit.calendarDays },
    { text: 'Business Day(s) After Creation', value: TaskDateOffsetUnit.businessDays },
];

export interface UserTaskAssignment {
    teamId?: number
    userName?: string
    teamName?: string
    patientId?: number
    prescriberId: number
    patient?: Patient
    prescriber?: Prescriber
    brandPortalUserEmail?: string
}

export interface BrandPortalUser {
    email?: string
    name?: string
    programId?: number
    programName?: string
}

export enum UserTaskRepeatMode {
    dayInterval,
    daysOfWeek,
    daysOfMonth
}

export interface UserTaskQuestionSet {
    id?: number;
    created?: Date;
    createdBy?: string;
    name?: string;
    entityType: EntityType;
    active: boolean;
    defaultScope?: string;

    questions: UserTaskQuestion[];
}

// nextQuestionPosition sentinel meaning "End Questions" - every consumer stops walking here,
// so a question left stranded behind a stale sentinel is never asked.
export const EndQuestionsPosition = -1;

// Positions are 1-based, so any non-positive pointer ends the chain.
export const endsQuestions = (position?: number): boolean => (position ?? EndQuestionsPosition) < 0;

export interface UserTaskQuestion {
    id?: number;
    taskQuestionSetId?: number;
    name?: string;
    text: string;
    typeId: UserTaskQuestionType;
    required: boolean;
    helpText?: string;
    position: number;
    nextQuestionPosition: number;
    internal: boolean;

    options: UserTaskQuestionOption[];
    acceptanceConfigurations?: AcceptanceConfiguration[];
}

export interface UserTaskQuestionOption {
    id?: number;
    taskQuestionId?: number;
    text: string;
    nextQuestionPosition: number;
    followUpTemplateId?: number;
    /** Days to wait before the follow-up task is created. Undefined creates it immediately. */
    followUpDelayDays?: number;
    followUpDelayAnchor?: TaskFollowUpDelayAnchor;
}

/** Date on the triggering task that a follow-up delay counts from. Values match the stored TINYINT. */
export enum TaskFollowUpDelayAnchor {
    completedDate = 0,
    createdDate = 1
}

export enum UserTaskQuestionType {
    multipleChoiceSingle,
    multipleChoiceMultiple,
    freeTextShort,
    freeTextLong,
    numeric,
    date,
    textCopy,
    acceptanceConfigurationsRequired
}

export interface UserTaskAnswer {
    id?: number;
    taskId?: number;
    taskQuestionId?: number;
    answer?: string;
}

export interface UserTaskTrigger {
    id?: number;
    name?: string;
    active: boolean;
    program?: Program;
    drugs?: DrugProgram[];
    taskTemplate?: UserTask;
    assignToPatient: boolean;

    triggerFillDateEnabled: boolean;
    triggerFillDateDays?: number;
    triggerShipDateEnabled: boolean;
    triggerShipDateDays?: number;
    triggerToStoreDateEnabled: boolean;
    triggerToStoreDateDays?: number;
    triggerToStoreId?: number;
    triggerShipDateDaySupplyPercentEnabled: boolean;
    triggerShipDateDaySupplyPercent?: number;
    triggerShipDateDaySupplyDaysEnabled: boolean;
    triggerShipDateDaySupplyDays?: number;
    triggerShipDateDaySupplyDaysAfter: boolean;
    triggerAfterPV1Enabled: boolean;
    triggerCopayEnabled: boolean;
    triggerCopayMinimum?: number | null;

    triggerLastFillHistoryEnabled: boolean;
    triggerLastFillHistoryDays?: number;

    exclusions?: any;

    created?: Date;
    createdBy?: string;
    updated?: Date;
    updatedBy?: string;

    sendPatientText: boolean;
    patientTextMessageTemplate?: string;
    treatExclusionsAsInclusions: boolean;
    matchOnAllModeExclusions: boolean;
}
